import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { channels } from '@/mocks/channels';
import { articles } from '@/mocks/articles';
import { brand } from '@/mocks/site';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [kw, setKw] = useState('');
  const [focused, setFocused] = useState(false);
  const nav = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const results = useMemo(() => {
    const q = kw.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 6);
  }, [kw]);

  const goContact = () => nav('/about#contact');

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? 'border-background-200/70 bg-background-50/95 backdrop-blur'
          : 'border-transparent bg-background-50'
      }`}
    >
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:h-[72px] md:px-6">
        {/* wordmark */}
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="返回热瓜速递首页">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-lg font-black text-background-50">
            瓜
          </span>
          <span className="leading-none">
            <span className="block text-xl font-black tracking-tight text-primary-700 md:text-2xl">
              热瓜速递
            </span>
            <span className="mt-0.5 hidden text-[10px] tracking-wide text-foreground-400 sm:block">
              {brand.sloganShort} · 八卦/明星/网红/影视/社会
            </span>
          </span>
        </Link>

        {/* desktop channel nav */}
        <nav className="ml-4 hidden items-center gap-1 lg:flex" aria-label="栏目导航">
          <Link
            to="/"
            className="rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
          >
            首页
          </Link>
          {channels.map((c) => (
            <Link
              key={c.slug}
              to={`/channel/${c.slug}`}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
            >
              {c.name}
            </Link>
          ))}
          <Link
            to="/about"
            className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
          >
            关于我们
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {/* search desktop */}
          <div ref={searchRef} className="relative hidden md:block">
            <div className="flex items-center rounded-full border border-background-300/70 bg-background-100/70 px-3 py-2 transition-colors focus-within:border-primary-300 focus-within:bg-background-50">
              <i className="ri-search-line mr-2 text-sm text-foreground-400" aria-hidden="true" />
              <input
                type="text"
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                onFocus={() => setFocused(true)}
                placeholder="搜索八卦 / 明星 / 热点…"
                className="w-40 bg-transparent text-sm outline-none placeholder:text-foreground-400"
                aria-label="站内搜索"
              />
            </div>
            {focused && kw.trim() && (
              <div className="absolute right-0 mt-2 w-80 overflow-hidden rounded-lg border border-background-200/80 bg-background-50 shadow-soft">
                {results.length > 0 ? (
                  <ul className="max-h-80 overflow-auto py-1">
                    {results.map((a) => (
                      <li key={a.id}>
                        <Link
                          to={`/news/${a.id}`}
                          onClick={() => setFocused(false)}
                          className="block px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          {a.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-3 py-3 text-sm text-foreground-400">没找到相关“瓜”，换个关键词试试</p>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={goContact}
            className="hidden whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 sm:inline-flex"
          >
            我要爆料
          </button>

          {/* hamburger */}
          <button
            type="button"
            aria-label="打开菜单"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            onClick={() => setOpen(true)}
          >
            <i className="ri-menu-line text-2xl" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 lg:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!open}
      >
        <div
          className="absolute inset-0 bg-foreground-950/40"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-background-50 transition-transform duration-300 ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between border-b border-background-200/70 px-4 py-3">
            <span className="text-lg font-black text-primary-700">热瓜速递</span>
            <button
              type="button"
              aria-label="关闭菜单"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-background-100"
              onClick={() => setOpen(false)}
            >
              <i className="ri-close-line text-2xl" aria-hidden="true" />
            </button>
          </div>

          {/* mobile search */}
          <div className="px-4 py-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const hit = articles.find((a) => a.title.includes(kw.trim()) || a.tags.includes(kw.trim()));
                if (hit) nav(`/news/${hit.id}`);
                setOpen(false);
              }}
            >
              <label className="flex items-center rounded-full border border-background-300/70 bg-background-100/70 px-3 py-2">
                <i className="ri-search-line mr-2 text-sm text-foreground-400" aria-hidden="true" />
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  placeholder="搜索你感兴趣的瓜…"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                  aria-label="站内搜索"
                />
              </label>
            </form>
          </div>

          <nav className="flex-1 overflow-auto px-2 py-2" aria-label="移动端导航">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-[15px] font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-home-5-line text-xl" aria-hidden="true" /> 首页
            </Link>
            {channels.map((c) => (
              <Link
                key={c.slug}
                to={`/channel/${c.slug}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-md px-3 py-3 text-[15px] font-semibold text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-fire-line text-xl text-primary-500" aria-hidden="true" /> {c.name}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-[15px] font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-group-line text-xl text-secondary-700" aria-hidden="true" /> 关于我们 / 投稿
            </Link>
          </nav>
          <div className="border-t border-background-200/70 p-4 text-center text-xs text-foreground-400">
            {brand.slogan}
          </div>
        </div>
      </div>
    </header>
  );
}